/**
 * Minimal column sets required for chart rendering
 * This optimization reduces data transfer by ~70-80%
 */

/**
 * Columns required for SignalProcessingChart.vue
 * Used to display TAC signal with quality markers and imputations
 */
export const SIGNAL_PROCESSING_CHART_COLUMNS = [
  // Time columns
  'datetime',
  'tMs',
  
  // TAC values
  'TAC',  // Final TAC (after imputation + savgol) - used for imputed data visualization
  'TAC_pre_imputation',
  'TAC_pre_savgol',
  'TAC_without_imputation',  // For hideRawLowQualityWithImputation checkbox
  
  // Quality flags - membership
  'imp_cand',
  'gap',
  'gap_without_imputation',
  'gap_imputed',
  'non_wear',
  'non_wear_without_imputation',
  'non_wear_imputed',
  'jump_imp_cand',
  'jump_without_imputation',
  'jump_imputed',
  'plummet_imp_cand',
  'plummet_without_imputation',
  'plummet_imputed',
  'extreme_negative_imp_cand',
  'extreme_negative_without_imputation',
  'extreme_negative_imputed',
  'proximal_low_quality_imp_cand',
  'proximal_low_quality_without_imputation',
  'proximal_low_quality_imputed'
]

/**
 * Columns required for NonWearDetectionChart.vue
 * Used to display temperature and device worn status
 */
export const NON_WEAR_DETECTION_CHART_COLUMNS = [
  // Time columns
  'datetime',
  'tMs',
  
  // Temperature and motion
  'Temperature_C',
  'Motion',
  
  // Device worn status
  'device_worn_model'
]

/**
 * Combined columns for both charts (union of both sets)
 * Use this when both charts might be displayed
 */
export const ALL_CHART_COLUMNS = [
  ...new Set([
    ...SIGNAL_PROCESSING_CHART_COLUMNS,
    ...NON_WEAR_DETECTION_CHART_COLUMNS
  ])
]

/**
 * Helper function to get column set based on chart types needed
 * @param {Object} options - Chart options
 * @param {boolean} options.signalProcessing - Include signal processing chart columns
 * @param {boolean} options.nonWearDetection - Include non-wear detection chart columns
 * @returns {Array<string>} Array of column names
 */
export function getChartColumns({ signalProcessing = true, nonWearDetection = true } = {}) {
  if (signalProcessing && nonWearDetection) {
    return ALL_CHART_COLUMNS
  } else if (signalProcessing) {
    return SIGNAL_PROCESSING_CHART_COLUMNS
  } else if (nonWearDetection) {
    return NON_WEAR_DETECTION_CHART_COLUMNS
  } else {
    return []
  }
}

/**
 * Create a cache manager for day/timerange data
 * @param {number} maxCachedItems - Maximum number of items to cache (default: 5)
 * @returns {Object} Cache manager with get, set, clear methods
 */
export function createDataCache(maxCachedItems = 5) {
  const cache = {}
  
  return {
    /**
     * Get cached data for a key
     * @param {string} key - Cache key (e.g., day date or time range string)
     * @returns {Array|null} Cached data or null if not found
     */
    get(key) {
      const entry = cache[key]
      if (entry) {
        console.log(`✅ Cache HIT for key: ${key}`)
        return entry.data
      }
      console.log(`❌ Cache MISS for key: ${key}`)
      return null
    },
    
    /**
     * Set cached data for a key
     * @param {string} key - Cache key
     * @param {Array} data - Data to cache
     */
    set(key, data) {
      // Remove oldest entry if at capacity
      if (Object.keys(cache).length >= maxCachedItems && !cache[key]) {
        const oldestKey = Object.keys(cache)
          .sort((a, b) => cache[a].timestamp - cache[b].timestamp)[0]
        delete cache[oldestKey]
        console.log(`🗑️ Cache full - removed oldest entry: ${oldestKey}`)
      }
      
      cache[key] = {
        data,
        timestamp: Date.now()
      }
      console.log(`💾 Cached data for key: ${key} (${Object.keys(cache).length}/${maxCachedItems} slots used)`)
    },
    
    /**
     * Clear all cached data
     */
    clear() {
      const count = Object.keys(cache).length
      Object.keys(cache).forEach(key => delete cache[key])
      console.log(`🧹 Cleared ${count} cache entries`)
    },
    
    /**
     * Get cache statistics
     * @returns {Object} Cache stats
     */
    getStats() {
      return {
        size: Object.keys(cache).length,
        maxSize: maxCachedItems,
        keys: Object.keys(cache)
      }
    }
  }
}

